if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.scale = 1;
      this.offset = { x: 0, y: 0 };
      this.pointers = new Map();
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.mobile.addEventListener('change', () => {
        if (this.hasAttribute('open')) this.activate(Math.max(0, this.mediaItems.findIndex((item) => item.classList.contains('active'))));
      });
      this.frameObserver = new ResizeObserver(() => this.positionControls());
      this.frameObserver.observe(this.querySelector('.product-media-modal__content'));
      this.querySelector('[data-media-zoom]')?.addEventListener('click', () => this.zoom(this.scale > 1 ? 1 : 2.5));
      this.addEventListener('pointerdown', (event) => {
        if (!this.mobile.matches || !event.target.matches('img.active')) return;
        event.preventDefault();
        event.target.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.start = { x: event.clientX, y: event.clientY, ox: this.offset.x, oy: this.offset.y };
        this.moved = false;
        if (this.pointers.size === 2) {
          const [a, b] = [...this.pointers.values()];
          this.pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y), scale: this.scale };
          this.moved = true;
        }
      });
      this.addEventListener('pointermove', (event) => {
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (this.pointers.size === 2 && this.pinch) {
          const [a, b] = [...this.pointers.values()];
          this.zoom(Math.max(1.5, Math.min(4, this.pinch.scale * Math.hypot(a.x - b.x, a.y - b.y) / this.pinch.distance)));
          this.moved = true;
        } else if (this.start) {
          const dx = event.clientX - this.start.x;
          const dy = event.clientY - this.start.y;
          if (Math.hypot(dx, dy) > 8) this.moved = true;
          if (this.scale > 1) {
            this.offset = { x: this.start.ox + dx, y: this.start.oy + dy };
            this.paintZoom();
          }
        }
      });
      this.addEventListener('pointerup', (event) => {
        if (!this.pointers.has(event.pointerId)) return;
        const pinching = Boolean(this.pinch);
        this.pointers.delete(event.pointerId);
        if (!pinching && this.scale === 1 && this.start) {
          const dx = event.clientX - this.start.x;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(event.clientY - this.start.y) * 1.5) this.move(dx < 0 ? 1 : -1);
        }
        if (!this.pointers.size) { this.pinch = null; this.start = null; }
        else {
          const next = [...this.pointers.values()][0];
          this.start = { ...next, ox: this.offset.x, oy: this.offset.y };
        }
      });
      this.addEventListener('pointercancel', () => { this.pointers.clear(); this.pinch = null; this.start = null; });
      this.addEventListener('dragstart', (event) => event.preventDefault());
      this.querySelector('[data-media-previous]')?.addEventListener('click', () => this.move(-1));
      this.querySelector('[data-media-next]')?.addEventListener('click', () => this.move(1));
      this.addEventListener('keydown', (event) => {
        if (!this.hasAttribute('open') || event.target.closest('video, iframe, model-viewer')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowLeft' ? -1 : 1);
        }
      });
    }
    zoom(scale, x, y) {
      const img = this.querySelector('img.active');
      if (!img) return;
      if (this.scale === 1 && scale > 1 && x !== undefined) {
        const rect = img.getBoundingClientRect();
        this.offset = { x: (rect.x + rect.width / 2 - x) * (scale - 1), y: (rect.y + rect.height / 2 - y) * (scale - 1) };
      }
      this.scale = scale;
      if (scale === 1) this.offset = { x: 0, y: 0 };
      if (scale > 1 && img.dataset.zoomSrc && !img.dataset.zoomLoaded) {
        img.dataset.zoomLoaded = 'true';
        img.removeAttribute('srcset');
        img.src = img.dataset.zoomSrc;
      }
      this.paintZoom();
    }
    paintZoom() {
      const img = this.querySelector('img.active');
      if (!img) return;
      const frame = this.querySelector('.product-media-modal__content');
      const xLimit = Math.max(0, (img.offsetWidth * this.scale - frame.clientWidth) / 2);
      const yLimit = Math.max(0, (img.offsetHeight * this.scale - frame.clientHeight) / 2);
      this.offset.x = Math.max(-xLimit, Math.min(xLimit, this.offset.x));
      this.offset.y = Math.max(-yLimit, Math.min(yLimit, this.offset.y));
      img.style.transform = `translate(${this.offset.x}px, ${this.offset.y}px) scale(${this.scale})`;
      this.classList.toggle('is-zoomed', this.scale > 1);
      const button = this.querySelector('[data-media-zoom]');
      if (button) button.setAttribute('aria-pressed', String(this.scale > 1));
      this.positionControls();
    }
    positionControls() {
      const img = this.querySelector('img.active');
      const height = this.querySelector('.product-media-modal__content').clientHeight;
      const ratio = img?.naturalWidth / img?.naturalHeight;
      const shownHeight = ratio ? Math.min(img.clientHeight, img.clientWidth / ratio) * this.scale : height;
      this.style.setProperty('--glimtiq-controls-bottom', `${Math.max(10, (height - shownHeight) / 2 + 10)}px`);
    }
    watchImage(img) {
      const frame = this.querySelector('.product-media-modal__content');
      frame.classList.remove('glimtiq-image-pending');
      frame.removeAttribute('aria-busy');
      if (img.tagName !== 'IMG' || img.complete) return;
      frame.classList.add('glimtiq-image-pending');
      frame.setAttribute('aria-busy', 'true');
      const done = () => {
        if (!img.classList.contains('active')) return;
        frame.classList.remove('glimtiq-image-pending');
        frame.removeAttribute('aria-busy');
        this.positionControls();
      };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }
    get mediaItems() {
      return Array.from(this.querySelector('.product-media-modal__content')?.children || [])
        .filter((element) => element.hasAttribute('data-media-id'));
    }
    show(opener) {
      super.show(opener);
      this.showActiveMedia();
    }
    showActiveMedia() {
      const index = this.mediaItems.findIndex((item) => item.dataset.mediaId === this.openedBy?.dataset.mediaId);
      this.activate(Math.max(0, index));
    }
    move(direction) {
      const items = this.mediaItems;
      if (!items.length) return;
      const index = items.findIndex((item) => item.classList.contains('active'));
      this.activate((index + direction + items.length) % items.length);
    }
    activate(index) {
      this.zoom(1);
      const items = this.mediaItems;
      if (!items[index]) return;
      window.pauseAllMedia();
      items.forEach((item, i) => {
        item.classList.toggle('active', i === index);
        item.setAttribute('aria-hidden', String(this.mobile.matches && i !== index));
        item.style.transform = '';
      });
      const active = items[index];
      if (this.mobile.matches) this.watchImage(active);
      else {
        const frame = this.querySelector('.product-media-modal__content');
        frame.classList.remove('glimtiq-image-pending');
        frame.removeAttribute('aria-busy');
      }
      this.positionControls();
      const zoomButton = this.querySelector('[data-media-zoom]');
      if (zoomButton) zoomButton.hidden = active.tagName !== 'IMG';
      if (zoomButton) zoomButton.setAttribute('aria-pressed', 'false');
      if (active.tagName === 'DEFERRED-MEDIA') active.loadContent(false);
      const content = this.querySelector('.product-media-modal__content');
      content.scrollTop = 0;
      content.scrollLeft = 0;
      this.offset = { x: 0, y: 0 };
      if (this.mobile.matches && active.tagName === 'IMG') this.zoom(2.5);
      if (!this.mobile.matches) {
        requestAnimationFrame(() => {
          content.scrollTop = Math.max(0, active.offsetTop - content.offsetTop + (active.offsetHeight - content.clientHeight) / 2);
        });
      }
      const current = this.querySelector('[data-media-current]');
      const total = this.querySelector('[data-media-total]');
      if (current) current.textContent = String(index + 1);
      if (total) total.textContent = String(items.length);
      this.querySelectorAll('[data-media-previous], [data-media-next]').forEach((button) => {
        button.disabled = items.length < 2;
      });
    }
  });
}
